import { get } from "lodash";
import { UnreachableCaseError } from "ts-essentials";
import {
  BinaryBooleanOperator,
  BOOLEAN_FUNCTIONS,
  BooleanType,
  type FormkitBoolean,
  type FormkitLiquidRenderer,
  hasLiquid,
  isFormkitBoolean,
  isFormkitReference,
  ReferenceType,
  UnaryBooleanOperator,
} from "../..";
import type * as Yup from "yup";

export type Context = { [key: string]: any };

export type ConditionalSchema = (
  config: Record<string, unknown>,
  ctx: Context,
) => Yup.AnySchema | undefined;

export type ConditionState =
  | Record<string, unknown>
  | ((key: string) => unknown);
type Condition = (
  state: ConditionState,
  context: Record<string, unknown>,
) => boolean | undefined;

export function resolveBooleanCondition(
  b: FormkitBoolean,
  renderer: FormkitLiquidRenderer,
): Condition {
  return (state, context) => {
    // Sometimes, the condition is just a raw boolean value
    if (typeof b === "boolean") return b;
    // Otherwise, we expect it to be a FormkitBoolean
    if (!isFormkitBoolean(b)) throw new UnreachableCaseError(b);

    // Simple callbacks to get the values from state or context
    const getState = (key: string) =>
      typeof state === "function" ? state(key) : get(state, key);

    const resolve = (entity: any, liquid: boolean) =>
      resolveEntity({ state: getState, context, entity, liquid, renderer });

    switch (b.type) {
      case BooleanType.Function: {
        const fn = BOOLEAN_FUNCTIONS[String(b.function)];
        return fn ? fn(resolve(b.variables, true)) || false : undefined;
      }
      case BooleanType.Unary: {
        const operand = resolve(b.operand, false);
        switch (b.operator) {
          case undefined:
            return !!operand;
          case UnaryBooleanOperator.Not:
            return !operand;
          default:
            throw new UnreachableCaseError(b);
        }
      }

      case BooleanType.Binary: {
        const left = resolve(b.leftOperand, false);
        const right = resolve(b.rightOperand, false);

        const nonNumeric =
          typeof left !== "number" || typeof right !== "number";

        switch (b.operator) {
          case BinaryBooleanOperator.GreaterThan:
            return nonNumeric ? undefined : left > right;
          case BinaryBooleanOperator.LessThan:
            return nonNumeric ? undefined : left < right;
          case BinaryBooleanOperator.GreaterThanOrEqual:
            return nonNumeric ? undefined : left >= right;
          case BinaryBooleanOperator.LessThanOrEqual:
            return nonNumeric ? undefined : left <= right;
          case BinaryBooleanOperator.Equals:
            return left === right;
          case BinaryBooleanOperator.NotEquals:
            return left !== right;
          case BinaryBooleanOperator.And:
            return !!left && !!right;
          case BinaryBooleanOperator.Or:
            return !!left || !!right;
          case BinaryBooleanOperator.Xor:
            return !left !== !right;
          default:
            throw new UnreachableCaseError(b);
        }
      }

      default:
        throw new UnreachableCaseError(b);
    }
  };
}

const IGNORE_LIQUID_FIELDS = ["template", "placeholder", "variables"];
// Keys whose subtree is resolved against a nested form's own state, not this one's.
export const SEPARATE_FORM_CONTEXTS = ["typeSpecs"];

export function resolveEntity(props: {
  state: ConditionState;
  context: Record<string, unknown>;
  entity: any;
  liquid: boolean;
  renderer: FormkitLiquidRenderer;
}): any {
  const { state, context, entity, liquid, renderer } = props;

  // Use lodash get
  const getState = (key: string) =>
    typeof state === "function" ? state(key) : get(state, key);

  // Shorthand for recursing
  const recurse = (entity: any, liquid: boolean) =>
    resolveEntity({ state: getState, context, entity, liquid, renderer });

  if (entity) {
    // Pass through primitive non-string values (booleans, numbers) as-is
    if (typeof entity !== "string" && typeof entity !== "object") {
      return entity;
    }

    // Strings are the only type where liquid templating actually gets applied
    if (typeof entity === "string") {
      if (liquid && hasLiquid(entity)) {
        try {
          return renderer.parseAndRenderSync(entity, context);
        } catch (error) {
          // biome-ignore lint/suspicious/noConsole: Formkit is a leaf package and cannot depend on the host logger.
          console.warn(`Failed to render Liquid template: ${entity}`, error);
        }
      }
      return entity;
    }

    // Recurse over every element of arrays
    if (Array.isArray(entity)) {
      return entity.map((item) => recurse(item, liquid));
    }

    if (typeof entity === "object") {
      // If this is a shallow reference
      if (isFormkitReference(entity)) {
        switch (entity.type) {
          case ReferenceType.GraphQL: {
            // Skip liquid parsing for graphql references
            // Parsing gql or its variables could cause unwanted errors
            return {
              query: entity.document,
              variables: recurse(entity.variables, false),
            };
          }
          case ReferenceType.Context:
            return recurse(get(context, entity.key), liquid);
          case ReferenceType.State:
            return recurse(getState(entity.key), liquid);
        }
      }
      // If this is a formkit boolean
      if (isFormkitBoolean(entity)) {
        return resolveBooleanCondition(entity, renderer)(getState, context);
      }

      // If we've made it this far, it's an object of unknown type
      // Recurse over every key value pair of objects
      return Object.fromEntries(
        Object.entries(entity).map(([key, value]) => [
          key,
          SEPARATE_FORM_CONTEXTS.includes(key)
            ? value
            : recurse(value, !IGNORE_LIQUID_FIELDS.includes(key)),
        ]),
      );
    }
  }

  // Fallback to unprocessed value
  return entity;
}

/**
 * Returns a function that "ORs" the given conditions.
 *
 * This is meant to be used with components that can be required for multiple
 * paths in a form. Think of paths as independent conditions in a form. So if a
 * MappingsSection is required when "mode" is "upsert" or "update", then we can
 * say that there are two paths to the MappingsSection component.
 */
export function or(
  a: ConditionalSchema,
  b: ConditionalSchema,
): ConditionalSchema {
  return (config, ctx) => a(config, ctx) || b(config, ctx);
}

/**
 * Returns a function that "ANDs" the given conditions.
 *
 * It accomplishes this task by requiring the latest condition to be true to
 * return the already existing conditional schema on a property, which may or may not
 * have other conditions wrapped around it.
 */
export function withPrerequisite(
  condition: Condition,
  conditionalSchema: ConditionalSchema,
): ConditionalSchema {
  return (config, ctx) =>
    condition(config, ctx) ? conditionalSchema(config, ctx) : undefined;
}
